import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import orderBy from 'lodash/fp/orderBy';
import { Table } from '@agconnections/grow-ui';
import LoadingTableRow from 'components/LoadingTableRow';
import TableHeader from 'components/LandingTable/components/TableHeader';
import usePestData from 'hooks/usePestData';
import ProductsTableRow from './components/ProductsTableRow';
import { APPLICATION_OPTIONS } from '../../../../../../../screens/Recommendations/Recommendation/helpers/applicationOptions';

const COLUMN_SORT_FUNCTIONS = {
  name: (direction, products) =>
    orderBy(
      [product => product.productName.toLowerCase()],
      direction,
      products
    ),
  applicationOption: (direction, products) =>
    orderBy(
      [
        product =>
          `${product.productRatePerAreaValue}_${product.productRatePerAreaUnit}`
      ],
      direction,
      products
    ),
  appliedArea: (direction, products) =>
    orderBy(
      [
        product =>
          `${product.productAppliedAreaValue}_${product.productAppliedAreaUnit}`
      ],
      direction,
      products
    ),
  appliedAreaPercent: (direction, products) =>
    orderBy([product => product.coveragePercent], direction, products),
  totalProduct: (direction, products) =>
    orderBy(
      [product => `${product.totalProductValue}_${product.totalProductUnit}`],
      direction,
      products
    )
};

const ProductsSection = ({ recommendation }) => {
  const applicationOption = Object.values(APPLICATION_OPTIONS).find(
    option => option.key === recommendation.applicationStrategy
  );
  const { pests, loading: loadingPests } = usePestData('', 0);
  const [sortBy] = useState();
  const [sortDirection] = useState();
  const [sortedProducts, setSortedProducts] = useState([]);
  const tableColumns = useMemo(() => {
    return [
      {
        field: 'name',
        title: `Product Name (${recommendation.recommendationProductEntries
          ?.length ?? 0})`,
        sortable: false
      },
      {
        field: 'applicationOption',
        title: applicationOption?.title ?? '',
        sortable: false
      },
      {
        field: 'appliedArea',
        title: 'Applied Area',
        sortable: false
      },
      {
        field: 'appliedAreaPercent',
        title: 'Applied Area %',
        sortable: false
      },
      {
        field: 'totalProduct',
        title: 'Total Product',
        sortable: false
      },
      {
        field: 'actions',
        title: '',
        sortable: false
      }
    ];
  }, [
    applicationOption?.title,
    recommendation.recommendationProductEntries?.length
  ]);

  useEffect(() => {
    const sortFunction = COLUMN_SORT_FUNCTIONS[sortBy ?? ''];
    if (sortFunction && sortDirection) {
      setSortedProducts(
        sortFunction(
          sortDirection,
          recommendation.recommendationProductEntries ?? []
        )
      );
    } else {
      setSortedProducts(recommendation.recommendationProductEntries ?? []);
    }
  }, [recommendation.recommendationProductEntries, sortBy, sortDirection]);

  return (
    <div data-testid="products-services-section" className="mb-6">
      <div className="flex justify-between pt-4 mb-8">
        <div data-testid="products-services-section-header">
          <div className="text-2xl font-bold">Products & Services</div>
          <div className="text-sm text-neutral-600">
            {`${applicationOption?.title} Application`}
          </div>
        </div>
      </div>
      <Table>
        <TableHeader sortBy={sortBy ?? ''} tableColumns={tableColumns} />
        {loadingPests ? (
          <LoadingTableRow numCells={tableColumns.length} />
        ) : (
          sortedProducts?.map((product, index) => (
            <ProductsTableRow
              // eslint-disable-next-line react/no-array-index-key
              key={`${product.productId}_${index}`}
              product={product}
              pests={pests}
            />
          ))
        )}
      </Table>
    </div>
  );
};

ProductsSection.propTypes = {
  recommendation: PropTypes.shape({
    applicationStrategy: PropTypes.string.isRequired,
    recommendationProductEntries: PropTypes.arrayOf(PropTypes.shape({}))
      .isRequired
  }).isRequired
};

export default ProductsSection;
